import { Box, Fade, IconButton, Modal, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import { styled, Theme } from '@mui/material/styles';
import React from 'react';
import { useResolveAsset } from '../context';

export interface MediaItem {
  /** Blog-relative path, e.g. "images/x.jpg" or "videos/x.mp4". */
  src: string;
  alt?: string;
  /** Shown under the media in the lightbox. Falls back to `alt`. */
  caption?: string;
  /** Overrides extension-based image/video detection. */
  type?: 'image' | 'video';
}

const VIDEO_EXT = /\.(mov|mp4|m4v|webm|ogg)$/i;
export const isVideo = (item: MediaItem): boolean =>
  item.type ? item.type === 'video' : VIDEO_EXT.test(item.src);

// Full-viewport overlay content. Modal supplies the dimmed backdrop + focus
// trap + Escape handling; this just centers the media and its chrome.
const Overlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none',
  padding: '4vmin',
});

const mediaSizing = (theme: Theme) => ({
  maxWidth: '92vw',
  maxHeight: '82vh',
  objectFit: 'contain' as const,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[24],
});
const FullImg = styled('img')(({ theme }) => mediaSizing(theme));
const FullVideo = styled('video')(({ theme }) => mediaSizing(theme));

const NavArrow = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  color: theme.palette.common.white,
  backgroundColor: 'rgba(255, 255, 255, 0.12)',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' },
}));

interface LightboxProps {
  items: MediaItem[];
  /** Index of the open item, or null when closed. */
  index: number | null;
  /** Set the open index; null closes. */
  onIndex: (i: number | null) => void;
}

/**
 * Centered overlay viewer shared by `Gallery` and `Figure`. Handles image and
 * video items, prev/next (when more than one), a caption + counter, and
 * keyboard navigation (arrows; Escape is handled by the underlying Modal).
 */
const Lightbox: React.FC<LightboxProps> = ({ items, index, onIndex }) => {
  const resolveAsset = useResolveAsset();
  const count = items.length;
  const open = index !== null && index >= 0 && index < count;
  const go = (i: number) => onIndex(((i % count) + count) % count); // wrap
  const close = () => onIndex(null);

  React.useEffect(() => {
    if (!open || index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(index + 1);
      else if (e.key === 'ArrowLeft') go(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, index]); // eslint-disable-line react-hooks/exhaustive-deps

  const item = open ? items[index as number] : null;

  return (
    <Modal open={open} onClose={close} closeAfterTransition>
      <Fade in={open}>
        <Overlay tabIndex={-1}>
          <IconButton
            aria-label="Close"
            onClick={close}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'common.white' }}
          >
            <Close />
          </IconButton>

          {count > 1 && (
            <NavArrow
              aria-label="Previous"
              onClick={() => index !== null && go(index - 1)}
              sx={{ left: '2vw' }}
            >
              <ChevronLeft fontSize="large" />
            </NavArrow>
          )}

          {item &&
            (isVideo(item) ? (
              <FullVideo key={item.src} src={resolveAsset(item.src)} controls playsInline />
            ) : (
              <FullImg src={resolveAsset(item.src)} alt={item.alt ?? ''} />
            ))}

          {count > 1 && (
            <NavArrow
              aria-label="Next"
              onClick={() => index !== null && go(index + 1)}
              sx={{ right: '2vw' }}
            >
              <ChevronRight fontSize="large" />
            </NavArrow>
          )}

          <Box sx={{ mt: 2, textAlign: 'center', color: 'common.white', maxWidth: '80vw' }}>
            {(item?.caption ?? item?.alt) && (
              <Typography variant="body2">{item?.caption ?? item?.alt}</Typography>
            )}
            {count > 1 && (
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {(index ?? 0) + 1} / {count}
              </Typography>
            )}
          </Box>
        </Overlay>
      </Fade>
    </Modal>
  );
};

export default Lightbox;
